import { Loader } from "apps/visits/packages/ui/src/components";
import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/core/shared/v2/ui/pagination";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import type { ItemListId } from "../../../tracking/create-ecommerce-event";
import { useGetProducts } from "../../api/get-products";
import type { PurchaseCatalogProduct } from "../../api/vouchers-distribution.types";
import { Search } from "../../components/search";
import { CatalogList } from "../../components/v2/catalog-list";
import { SideCart } from "../../components/v2/side-cart";
import { useCatalogSearch } from "../../hooks/use-catalog-search";
import { useFlowPath } from "../../hooks/use-flow-path";
import type { BloodTestPackage } from "../../models/blood-test-package";
import type { AddPurchaseCatalogProductResult } from "../../store/use-cart-state";
import { useCartState } from "../../store/use-cart-state";
import { useSearchState } from "../../store/use-search-state";
import { useVouchersDistributionState } from "../../store/use-vouchers-distribution-state";
import { beginCheckoutEvent, safeTrackEvent } from "../../tracking/events";
import { isSamePurchaseCatalogLine, purchaseCatalogRowKey } from "../../utils/purchase-catalog-line";
import { configName } from "../config/config-name";
import { routes } from "../config/routes";
import { useCreateOrderFromRecommendation } from "../hooks/use-create-order-from-recommendation";
import { usePrefillFromPreparedCart } from "../hooks/use-prefill-from-prepared-cart";

const ITEMS_PER_PAGE = 12;

export const Catalog = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const flowPath = useFlowPath();
  const { searchTerm, searchResults, clearSearch, isSearching } = useSearchState<BloodTestPackage>();
  const { products, addPurchaseCatalogProduct, removeProduct, clearCart, setProductsLimit } = useCartState();
  const { voucherDistribution } = useVouchersDistributionState();
  const [currentPage, setCurrentPage] = useState(1);
  useCreateOrderFromRecommendation();
  usePrefillFromPreparedCart();

  useEffect(() => {
    setCurrentPage(1);
  }, []);

  useEffect(() => {
    setProductsLimit(undefined);
  }, [setProductsLimit]);

  const productsQuery = useGetProducts({
    slug: voucherDistribution?.slug || "",
    configName,
  });

  const catalogProducts: PurchaseCatalogProduct[] = useMemo(() => {
    const products = productsQuery.data?.products || [];
    return [...products].sort((a, b) => {
      const rank = (row: PurchaseCatalogProduct) => (row.productType === "blood-test-package" ? 0 : 1);
      return rank(a) - rank(b);
    });
  }, [productsQuery.data?.products]);

  const { search, clearSearchResults } = useCatalogSearch({
    products: catalogProducts,
  });

  const notifyPurchaseCatalogAddResult = useCallback(
    (result: AddPurchaseCatalogProductResult) => {
      const softToast = (message: string) =>
        toast(message, {
          duration: 5000,
          style: {
            background: "#f4f4f5",
            color: "#27272a",
            border: "1px solid #e4e4e7",
          },
        });

      if (result.status === "blocked_test_in_package") {
        softToast(t("VOUCHERS_DISTRIBUTION.PURCHASE_PRODUCTS_CART.BLOCKED_TEST_ALREADY_IN_PACKAGE"));
        return;
      }
      if (result.status === "blocked_products_limit") {
        softToast(t("VOUCHERS_DISTRIBUTION.PURCHASE_PRODUCTS_CART.PRODUCTS_LIMIT_REACHED"));
        return;
      }
      if (result.status === "package_added_with_removals") {
        const n = result.removedSinglesQuantity;
        softToast(
          n === 1
            ? t("VOUCHERS_DISTRIBUTION.PURCHASE_PRODUCTS_CART.SINGLE_TESTS_REMOVED_ONE")
            : t("VOUCHERS_DISTRIBUTION.PURCHASE_PRODUCTS_CART.SINGLE_TESTS_REMOVED_OTHER", { count: n }),
        );
      }
    },
    [t],
  );

  const handleAddPurchaseCatalogProduct = useCallback(
    (product: PurchaseCatalogProduct, itemListId?: ItemListId, index?: number) => {
      const result = addPurchaseCatalogProduct({ ...product, item_list_id: itemListId, index });
      notifyPurchaseCatalogAddResult(result);
    },
    [addPurchaseCatalogProduct, notifyPurchaseCatalogAddResult],
  );

  const goToNextStep = () => {
    clearSearch();
    if (products.length > 0) {
      const analyticsProducts = products.map((p) => ({
        ...(p as unknown as BloodTestPackage),
        productType: p.productType,
        quantity: p.quantity,
      }));
      const totalPrice = products.reduce((acc, p) => acc + Number(p.grossPrice) * p.quantity, 0);
      safeTrackEvent(
        () => beginCheckoutEvent(analyticsProducts, { value: totalPrice }, { numberOfProducts: products.length }),
        "begin_checkout",
        "catalog continue",
      );
    }
    history.push(flowPath(routes.checkout.raw()));
  };

  const filteredPackages = useMemo(() => {
    if (searchTerm.length >= 2) {
      const searchResultKeys = new Set(searchResults.map((r) => purchaseCatalogRowKey(r)));
      return catalogProducts.filter((p) => searchResultKeys.has(purchaseCatalogRowKey(p)));
    }

    return catalogProducts;
  }, [catalogProducts, searchTerm, searchResults]);

  const totalPages = Math.ceil((filteredPackages?.length || 0) / ITEMS_PER_PAGE);

  const paginatedPackages = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPackages?.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPackages, currentPage]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const productQuantities = useMemo(() => {
    return products.reduce(
      (acc, product) => {
        acc[purchaseCatalogRowKey(product)] = product.quantity || 1;
        return acc;
      },
      {} as Record<string, number>,
    );
  }, [products]);

  if (!voucherDistribution) return null;

  const handleRemoveProduct = (product: BloodTestPackage) => {
    const cartProduct = products.find((p) => isSamePurchaseCatalogLine(p, product));
    if (cartProduct) {
      removeProduct(cartProduct as unknown as BloodTestPackage);
    }
  };

  const renderPaginationItems = () => {
    const items = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={currentPage === i}
              linkProps={{
                href: "#",
                onClick: (e) => {
                  e.preventDefault();
                  handlePageChange(i);
                },
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    } else {
      let startPage = Math.max(1, currentPage - 2);
      let endPage = Math.min(totalPages, currentPage + 2);

      if (startPage === 1) {
        endPage = Math.min(totalPages, maxVisiblePages);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - maxVisiblePages + 1);
      }

      if (startPage > 1) {
        items.push(
          <PaginationItem key={1}>
            <PaginationLink
              isActive={currentPage === 1}
              linkProps={{
                href: "#",
                onClick: (e) => {
                  e.preventDefault();
                  handlePageChange(1);
                },
              }}
            >
              1
            </PaginationLink>
          </PaginationItem>,
        );
        if (startPage > 2) {
          items.push(
            <PaginationItem key="ellipsis-start">
              <PaginationEllipsis />
            </PaginationItem>,
          );
        }
      }

      for (let i = startPage; i <= endPage; i++) {
        items.push(
          <PaginationItem key={i}>
            <PaginationLink
              isActive={currentPage === i}
              linkProps={{
                href: "#",
                onClick: (e) => {
                  e.preventDefault();
                  handlePageChange(i);
                },
              }}
            >
              {i}
            </PaginationLink>
          </PaginationItem>,
        );
      }

      if (endPage < totalPages) {
        if (endPage < totalPages - 1) {
          items.push(
            <PaginationItem key="ellipsis-end">
              <PaginationEllipsis />
            </PaginationItem>,
          );
        }
        items.push(
          <PaginationItem key={totalPages}>
            <PaginationLink
              isActive={currentPage === totalPages}
              linkProps={{
                href: "#",
                onClick: (e) => {
                  e.preventDefault();
                  handlePageChange(totalPages);
                },
              }}
            >
              {totalPages}
            </PaginationLink>
          </PaginationItem>,
        );
      }
    }

    return items;
  };

  return (
    <IntersectionFadeIn className={cn(["mx-auto w-full max-w-[1376px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12"])}>
      <div className="">
        <div className={cn("mb-6 flex flex-col items-start")}>
          <h1 className="font-serif text-2xl lg:text-4xl">{t(`cmsTranslations.purchase_products.catalog.title`)}</h1>
          <p className="mt-2 max-w-3xl text-sm lg:mt-4 lg:text-lg">
            {t(`cmsTranslations.purchase_products.catalog.description`)}
          </p>
        </div>
        <SidebarLayout>
          {!productsQuery.isLoading ? (
            <IntersectionFadeIn className="flex w-full flex-col gap-8">
              <div className="lg:mb-2">
                <Search search={search} clearSearchResults={clearSearchResults} />
              </div>
              {isSearching ? (
                <div className="inset-0 z-50 flex h-[calc(100vh-36rem)] w-full items-center justify-center">
                  <Loader />
                </div>
              ) : (
                paginatedPackages && (
                  <>
                    {paginatedPackages.length > 0 ? (
                      <CatalogList
                        simplified
                        vouchers={paginatedPackages}
                        getRowKey={purchaseCatalogRowKey}
                        allowMultiple={true}
                        enableQuantity={false}
                        selectedProductUuids={products.map((p) => purchaseCatalogRowKey(p))}
                        onAddProduct={(product, itemListId, index) =>
                          handleAddPurchaseCatalogProduct(product as PurchaseCatalogProduct, itemListId, index)
                        }
                        onRemoveProduct={handleRemoveProduct}
                        onClearCart={clearCart}
                        productQuantities={productQuantities}
                        itemListId={searchTerm.length >= 2 ? "wyniki_wyszukiwania" : "listing"}
                      />
                    ) : (
                      <div className="flex h-[calc(100vh-24rem)] flex-col items-center justify-center py-12 text-center">
                        <p className="mb-4 font-medium text-lg">{t("VOUCHERS_DISTRIBUTION.SEARCH.NO_RESULTS")}</p>
                        <Button onClick={clearSearchResults}>{t("VOUCHERS_DISTRIBUTION.SEARCH.CLEAR")}</Button>
                      </div>
                    )}
                    {totalPages > 1 && (
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <PaginationPrevious
                              linkProps={{
                                href: "#",
                                onClick: (e) => {
                                  e.preventDefault();
                                  if (currentPage > 1) handlePageChange(currentPage - 1);
                                },
                              }}
                              className={currentPage === 1 ? "pointer-events-none opacity-50" : ""}
                            />
                          </PaginationItem>
                          {renderPaginationItems()}
                          <PaginationItem>
                            <PaginationNext
                              linkProps={{
                                href: "#",
                                onClick: (e) => {
                                  e.preventDefault();
                                  if (currentPage < totalPages) handlePageChange(currentPage + 1);
                                },
                              }}
                              className={currentPage === totalPages ? "pointer-events-none opacity-50" : ""}
                            />
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    )}
                  </>
                )
              )}
            </IntersectionFadeIn>
          ) : (
            <div className="inset-0 z-50 flex h-[calc(100vh-24rem)] w-full items-center justify-center">
              <Loader />
            </div>
          )}
          <SideCart ctaText={t("COMMONS.GENERAL.CONTINUE")} onNextStep={goToNextStep} />
        </SidebarLayout>
      </div>
    </IntersectionFadeIn>
  );
};
